// Give a booking a stylist: stylists free at the booked time come first (one click), or pick another
// free time that day (the booking moves and the customer is told). Booking a slot makes it taken.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatDate, formatPhone } from '../services/api';
import { Loader, Modal, SourceBadge, Spinner } from './ui';

const AT_TIME = {
  free: { label: 'Free', tone: 'ok' },
  current: { label: 'Current stylist', tone: 'gold' },
  booked: { label: 'Busy', tone: 'danger' },
  off: { label: 'Marked off', tone: 'muted' },
  none: { label: 'Not working', tone: 'muted' },
};

export default function AssignStylistModal({ appointmentId, onClose, onAssigned }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState(null); // { slot_id, staff_name, time, label, moves }
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .assignOptions(appointmentId)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [appointmentId]);

  const appt = data?.appointment;
  const assigned = Boolean(appt?.staff_name);

  const save = async () => {
    if (!choice || saving) return;
    setSaving(true);
    try {
      const res = await api.assignStylist(appointmentId, choice.slot_id, notify);
      toast.success([`${choice.staff_name} assigned${res.time_changed ? ` at ${choice.label}` : ''}.`, ...(res.notes || [])].join(' '));
      onAssigned?.(res);
      onClose();
    } catch (e) {
      toast.error(e.message);
      api.assignOptions(appointmentId).then(setData).catch(() => {});
      setChoice(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={assigned ? 'Change stylist or time' : 'Assign a stylist'} onClose={onClose} size="wide" top>
      {error ? (
        <div className="nye-callout nye-callout--danger">{error}</div>
      ) : !data ? (
        <Loader />
      ) : (
        <>
          <div className="nye-assign-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nye-strong" style={{ fontSize: 15 }}>
                {appt.customer_name || formatPhone(appt.customer_phone)} · {appt.service_name}
              </div>
              <div className="nye-small nye-muted">
                {formatDate(appt.starts_at)}, {data.time_label} · {assigned ? `with ${appt.staff_name}` : 'any available stylist, waiting for a stylist'}
              </div>
            </div>
            <SourceBadge source={appt.source} showLabel />
          </div>

          {appt.status !== 'booked' ? (
            <div className="nye-callout nye-mt-16">Only a booked appointment can be given a stylist. This one is {appt.status.replace('_', ' ')}.</div>
          ) : data.stylists.length === 0 ? (
            <div className="nye-callout nye-mt-16">No active stylists. Add one in Staff Availability → Stylists.</div>
          ) : (
            <div className="nye-assign-list nye-mt-16">
              {data.stylists.map((s) => {
                const at = AT_TIME[s.at_time.state] || AT_TIME.none;
                const others = s.free_times.filter((t) => t.slot_id !== s.at_time.slot_id);
                const sameSelected = choice && choice.slot_id === s.at_time.slot_id;
                return (
                  <div key={s.id} className={`nye-assign-row${s.at_time.state === 'free' ? ' free' : ''}`}>
                    <div className="nye-assign-who">
                      <div className="nye-profile-dot">{s.name[0].toUpperCase()}</div>
                      <div>
                        <div className="nye-strong">{s.name}</div>
                        <span className={`nye-pill nye-pill--sm nye-pill--${at.tone}`}>
                          {at.label} at {data.time_label}
                        </span>
                      </div>
                    </div>
                    <div className="nye-assign-actions">
                      {s.at_time.state === 'free' ? (
                        <button
                          type="button"
                          className={`nye-btn nye-btn--sm ${sameSelected ? 'nye-btn--primary' : 'nye-btn--outline'}`}
                          onClick={() => setChoice({ slot_id: s.at_time.slot_id, staff_name: s.name, label: data.time_label, moves: false })}
                        >
                          {sameSelected ? '✓ Selected' : `Assign at ${data.time_label}`}
                        </button>
                      ) : null}
                      {others.length ? (
                        <div className="nye-time-grid nye-time-grid--sm">
                          <span className="nye-small nye-muted" style={{ alignSelf: 'center' }}>
                            {s.at_time.state === 'free' ? 'or move to' : 'Free at'}
                          </span>
                          {others.map((t) => (
                            <button
                              key={t.slot_id}
                              type="button"
                              className={`nye-time-chip${choice?.slot_id === t.slot_id ? ' selected' : ''}`}
                              onClick={() => setChoice({ slot_id: t.slot_id, staff_name: s.name, label: t.label, moves: t.time !== data.time })}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      ) : s.at_time.state !== 'free' ? (
                        <span className="nye-small nye-muted">No free times on this day</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {choice ? (
            <div className={`nye-callout nye-mt-16${choice.moves ? '' : ' nye-callout--info'}`}>
              {choice.moves ? (
                <>
                  The booking moves from {data.time_label} to <strong>{choice.label}</strong> with <strong>{choice.staff_name}</strong>. Reminders move too.
                  {notify ? ' The customer gets the "time changed" WhatsApp message.' : ''}
                </>
              ) : (
                <>
                  <strong>{choice.staff_name}</strong>'s {choice.label} slot is booked for this customer, so nobody else can take it.
                  {notify ? ' The customer gets the "stylist assigned" WhatsApp message.' : ''}
                </>
              )}
            </div>
          ) : null}

          <label className="nye-check nye-mt-16">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            <span>Send the customer a WhatsApp update</span>
          </label>

          <div className="nye-booking-foot">
            <div style={{ flex: 1 }} />
            <button type="button" className="nye-btn nye-btn--outline" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="nye-btn nye-btn--primary" disabled={!choice || saving || appt.status !== 'booked'} onClick={save}>
              {saving ? <Spinner light /> : null}
              {choice ? (choice.moves ? `Move and assign ${choice.staff_name}` : `Assign ${choice.staff_name}`) : 'Pick a stylist'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
